import { useEffect, useState } from 'react';
import { Plus, Check, Loader2, Upload, Download, Trash2, StickyNote, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/App';
import AdminLayout from '@/components/AdminLayout';

const STATUS_LABELS = { in_progress: 'IN PROGRESS', review: 'IN REVIEW', done: 'DELIVERED' };

export function ProjectFiles({ projectId }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = () => api.get(`/projects/${projectId}/files`).then((r) => { setFiles(r.data); setErr(''); })
    .catch((e) => setErr(e?.response?.data?.detail || ''));
  useEffect(() => { load(); }, [projectId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error('Files must be 50 MB or smaller.'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/projects/${projectId}/files`, form);
      toast.success(`${file.name} uploaded.`);
      load();
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/project-files/${id}`);
      setFiles((f) => f.filter((x) => x.id !== id));
    } catch { toast.error('Could not remove the file.'); }
  };

  const download = async (id, name) => {
    try {
      const res = await api.get(`/project-files/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed.'); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-800/60">
      <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mb-3">PROJECT FILES (50 MB MAX)</p>
      {files.length > 0 && (
        <div className="space-y-2 mb-3">
          {files.map((f) => (
            <div key={f.id} data-testid="project-file-row" className="flex items-center justify-between gap-3 border border-slate-800 rounded-lg bg-[#0B1528]/70 px-3 py-2">
              <span className="text-xs text-slate-300 truncate">{f.original_filename}
                <span className="text-slate-600 ml-2">{Math.max(1, Math.round((f.size || 0) / 1024))} KB</span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <button data-testid="project-file-download" onClick={() => download(f.id, f.original_filename)}
                  className="text-slate-400 hover:text-amber-300 transition-colors" title="Download"><Download size={14} /></button>
                <button data-testid="project-file-delete" onClick={() => remove(f.id)}
                  className="text-slate-600 hover:text-red-400 transition-colors" title="Remove"><Trash2 size={14} /></button>
              </span>
            </div>
          ))}
        </div>
      )}
      {err && <p className="text-xs text-amber-300/80 mb-2">{err}</p>}
      <label data-testid="project-file-upload-label"
        className={`inline-flex items-center gap-2 text-xs border border-amber-500/40 text-amber-300 rounded-full px-4 py-2 cursor-pointer hover:bg-amber-500/10 transition-colors ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload file
        <input data-testid="project-file-upload-input" type="file" className="hidden" onChange={upload}
          accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.ppt,.pptx,.doc,.docx,.txt,.rtf,.ai,.psd,.zip,.key,.fig" />
      </label>
    </div>
  );
}

export function StaffProjectCard({ p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [newMilestone, setNewMilestone] = useState('');
  const [note, setNote] = useState(p.dev_note || '');
  const noteDirty = note !== (p.dev_note || '');

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${p.id}`, body);
      onChanged();
    } catch { toast.error('Could not update project.'); } finally { setBusy(false); }
  };

  const doneCount = (p.milestones || []).filter((m) => m.done).length;
  const total = (p.milestones || []).length;

  return (
    <div data-testid="staff-project-card" className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-display font-semibold text-lg">{p.title}</h3>
          <p className="text-xs text-slate-500 mt-1">{p.customer_email}</p>
        </div>
        <select data-testid="project-status-select" value={p.status} disabled={busy}
          onChange={(e) => patch({ status: e.target.value })}
          className="bg-[#0B1528] border border-slate-700 rounded-lg text-xs px-3 py-2 text-slate-200 outline-none">
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {total > 0 && (
        <div className="mb-4">
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] transition-all duration-700" style={{ width: `${(doneCount / total) * 100}%` }} />
          </div>
          <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mt-2">{doneCount} / {total} MILESTONES</p>
        </div>
      )}
      <ul className="space-y-2">
        {(p.milestones || []).map((m, i) => (
          <li key={i} className="flex items-center gap-3 text-sm">
            <button data-testid="milestone-toggle" disabled={busy} onClick={() => patch({ toggle_milestone: i })}
              className={`h-5 w-5 rounded border flex items-center justify-center transition-colors ${m.done ? 'border-amber-400 bg-amber-500/20' : 'border-slate-600 hover:border-amber-500/50'}`}>
              {m.done && <Check size={12} className="text-amber-300" />}
            </button>
            <span className={m.done ? 'text-slate-500 line-through' : 'text-slate-300'}>{m.label}</span>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-4">
        <input data-testid="milestone-add-input" value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)}
          placeholder="Add milestone…"
          className="flex-1 bg-[#0B1528]/70 border border-slate-700/60 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-500/60 placeholder:text-slate-600" />
        <button data-testid="milestone-add-button" disabled={busy || !newMilestone.trim()}
          onClick={() => { patch({ add_milestone: newMilestone.trim() }); setNewMilestone(''); }}
          className="text-xs border border-amber-500/40 text-amber-300 rounded-lg px-4 hover:bg-amber-500/10 transition-colors disabled:opacity-40">
          <Plus size={14} />
        </button>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-800/60">
        <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mb-2 flex items-center gap-2">
          <StickyNote size={12} /> DEV NOTE {p.dev_note_by ? `· BY ${p.dev_note_by.toUpperCase()}` : ''}
        </p>
        <div className="flex gap-2">
          <input data-testid="project-note-input" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Notes for the developer — changes required, links, details…"
            className="flex-1 bg-[#0B1528]/70 border border-slate-700/60 rounded-lg px-3 py-2 text-xs outline-none focus:border-amber-500/60 placeholder:text-slate-600" />
          <button data-testid="project-note-save-button" disabled={!noteDirty || busy} onClick={() => patch({ note })}
            className="text-xs border border-amber-500/40 text-amber-300 rounded-lg px-4 hover:bg-amber-500/10 transition-colors disabled:opacity-40">
            Save
          </button>
        </div>
      </div>
      <ProjectFiles projectId={p.id} />
    </div>
  );
}

export default function AdminProjects() {
  const [projects, setProjects] = useState(null);
  const [form, setForm] = useState({ customer_email: '', title: '', milestones: '' });
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/projects').then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  const createProject = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/projects', {
        customer_email: form.customer_email, title: form.title,
        milestones: form.milestones.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      toast.success('Project created — the customer sees it on their next sign-in.');
      setForm({ customer_email: '', title: '', milestones: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create project.');
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600';

  return (
    <AdminLayout title="Client Projects" subtitle="Milestones, developer notes and client files. Customers upload logos, designs and requirement docs here too.">
      <form onSubmit={createProject} className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6 space-y-3 mb-8">
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="project-customer-email-input" required type="email" placeholder="Customer email (their sign-in)" value={form.customer_email}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className={inputCls} />
          <input data-testid="project-title-input" required placeholder="Project title — e.g. Alvarez Landscaping site" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
        </div>
        <textarea data-testid="project-milestones-input" rows={3} placeholder={'Milestones, one per line:\nDiscovery call\nDesign approved\nBuild complete\nLaunched'} value={form.milestones}
          onChange={(e) => setForm({ ...form, milestones: e.target.value })} className={inputCls} />
        <button data-testid="project-create-button" type="submit" disabled={busy}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-6 py-2.5 text-sm disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create project
        </button>
      </form>

      {projects === null ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="text-amber-400 animate-spin" /></div>
      ) : projects.length === 0 ? (
        <p className="text-sm text-slate-500 border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-8 text-center">
          <FolderKanban size={18} className="inline-block mr-2 text-amber-400" />No client projects yet — create one above.
        </p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {projects.map((p) => <StaffProjectCard key={p.id} p={p} onChanged={load} />)}
        </div>
      )}
    </AdminLayout>
  );
}
